"use client";

import React from "react";
import { motion } from "framer-motion";
import Image from "next/image";

const cormorant = {
    className: "font-cormorant font-serif",
};

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7 },
};

export interface GuestiqoShowcaseBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    browserUrl?: string;
    imageSrc?: string;
    imageAlt?: string;
}

export default function GuestiqoShowcaseBlock({
    tag = "// Production Endpoint",
    title = "The Marketing Platform",
    description = "We designed and engineered the conversion-focused guestiqo.uk marketing platform, combining warm hospitality branding with clear, editorial benefit-focused layouts that drive operator leads.",
    browserUrl = "https://guestiqo.uk",
    imageSrc = "/images/guestiqo_website_screenshot.png",
    imageAlt = "Guestiqo Marketing Website Design"
}: GuestiqoShowcaseBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#FDFBF7] w-full" data-header-theme="dark">
            <div className="container mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-12">
                    <div className="lg:col-span-6">
                        {tag && <span className="text-xs font-mono text-[#8C8276] uppercase tracking-widest block mb-4">{tag}</span>}
                        {title && <h2 className={`${cormorant.className} text-4xl md:text-5xl font-light text-[#1C1C1C] leading-tight`}>{title}</h2>}
                    </div>
                    <div className="lg:col-span-6">
                        {description && (
                            <p className="text-[#5E5E5E] text-sm leading-relaxed max-w-xl font-light font-sans">
                                {description}
                            </p>
                        )}
                    </div>
                </div>

                <motion.div {...fadeUp} className="relative group">
                    {/* Curated Light Browser Shell */}
                    <div className="rounded-2xl overflow-hidden border border-[#1C1C1C]/10 bg-white shadow-[0_25px_60px_rgba(28,28,28,0.04)]">
                        {/* Browser Header */}
                        <div className="flex items-center gap-2 px-6 py-4 bg-[#F7F3EB]/40 border-b border-[#1C1C1C]/5">
                            <div className="flex gap-1.5">
                                <div className="w-3 h-3 rounded-full bg-[#E5E1D8]" />
                                <div className="w-3 h-3 rounded-full bg-[#E5E1D8]" />
                                <div className="w-3 h-3 rounded-full bg-[#E5E1D8]" />
                            </div>
                            {browserUrl && (
                                <div className="flex-1 flex justify-center">
                                    <div className="bg-[#FAF7F2] border border-[#1C1C1C]/5 rounded-md px-5 py-1 text-[10px] text-[#8C8276] font-mono select-none">
                                        {browserUrl}
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* Browser Screenshot */}
                        <div className="relative aspect-[16/9] overflow-hidden">
                            {imageSrc && (
                                <Image 
                                    src={imageSrc} 
                                    alt={imageAlt} 
                                    fill 
                                    className="object-cover object-top group-hover:scale-[1.01] transition-transform duration-1000"
                                />
                            )}
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
